import {BaseModel, createFieldDef, FieldDefinition, ModelFieldType} from './baseModel';

import {BOL_REVIEWS_URL_FRAGMENT} from '../util/constants';

import {generateComponentId} from '@dvrd/dvr-controls';

import IDate from '@dvrd/idate';

interface Props {
    id?: string;
    created_at?: string;
    updated_at?: string;
    title?: string | null;
    ean?: string | null;
    global_id?: string;
    image_url?: string | null;
    rating?: number;
    review_title?: string;
    author?: string;
    review_date?: string;
    body?: string;
    num_positive?: number;
    num_negative?: number;
    pros?: Array<string>;
    cons?: Array<string>;
    age?: string | null;
    city?: string | null;
}

interface ProductReviewFields {
    id: string;
    created_at: string;
    updated_at: string;
    title: string | null;
    ean: string | null;
    global_id: string;
    image_url: string | null;
    rating: number;
    review_title: string;
    author: string;
    review_date: string;
    body: string;
    num_positive: number;
    num_negative: number;
    pros: Array<string>;
    cons: Array<string>;
    age: string | null;
    city: string | null;
}

const fields: ProductReviewFields = {
    id: '',
    created_at: '',
    updated_at: '',
    title: null,
    ean: null,
    global_id: '',
    image_url: null,
    rating: 0,
    review_title: '',
    author: '',
    review_date: '',
    body: '',
    num_positive: 0,
    num_negative: 0,
    pros: [],
    cons: [],
    age: null,
    city: null
};

const fieldsDefinition: {[key in keyof ProductReviewFields]: FieldDefinition} = {
    id: createFieldDef(ModelFieldType.STRING),
    created_at: createFieldDef(ModelFieldType.STRING),
    updated_at: createFieldDef(ModelFieldType.STRING, undefined, true),
    title: createFieldDef(ModelFieldType.STRING, null, true),
    ean: createFieldDef(ModelFieldType.STRING, null, true),
    global_id: createFieldDef(ModelFieldType.STRING),
    image_url: createFieldDef(ModelFieldType.STRING, null, true),
    rating: createFieldDef(ModelFieldType.NUMBER),
    review_title: createFieldDef(ModelFieldType.STRING),
    author: createFieldDef(ModelFieldType.STRING),
    review_date: createFieldDef(ModelFieldType.STRING),
    body: createFieldDef(ModelFieldType.STRING),
    num_positive: createFieldDef(ModelFieldType.NUMBER),
    num_negative: createFieldDef(ModelFieldType.NUMBER),
    age: createFieldDef(ModelFieldType.STRING, null, true),
    city: createFieldDef(ModelFieldType.STRING, null, true),
    cons: createFieldDef(ModelFieldType.ARRAY),
    pros: createFieldDef(ModelFieldType.ARRAY)
};

const ignoreProps: (keyof ProductReviewFields)[] = [];

export default class ProductReview extends BaseModel<Props, ProductReviewFields> {
    static dateFormat = 'D MMMM YYYY';

    constructor(props?: Props) {
        super(ignoreProps, fieldsDefinition, fields, props);
        this.build(props);
        this.fields.id = generateComponentId();
    }

    get id(): string {
        return this.fields.id;
    }

    get createdAt(): string {
        return this.fields.created_at;
    }

    get createdAtMoment(): IDate {
        return new IDate(this.createdAt);
    }

    get updatedAt(): string {
        return this.fields.updated_at;
    }

    get updatedAtMoment(): IDate {
        return new IDate(this.updatedAt);
    }

    get title(): string | null {
        return this.fields.title;
    }

    get ean(): string | null {
        return this.fields.ean;
    }

    get globalId(): string {
        return this.fields.global_id;
    }

    get imageUrl(): string | null {
        return this.fields.image_url;
    }

    get rating(): number {
        return this.fields.rating;
    }

    get reviewTitle(): string {
        return this.fields.review_title;
    }

    get author(): string {
        return this.fields.author;
    }

    get reviewDateString(): string {
        return this.fields.review_date;
    }

    get reviewDate(): IDate {
        return new IDate(this.fields.review_date);
    }

    get body(): string {
        return this.fields.body;
    }

    get pros(): Array<string> {
        return this.fields.pros;
    }

    get cons(): Array<string> {
        return this.fields.cons;
    }

    get age(): string | null {
        return this.fields.age;
    }

    get city(): string | null {
        return this.fields.city;
    }

    /** bol.com product page deep-linked to the reviews section. The title is
     *  a URL path segment: slashes would read as extra segments and reserved
     *  characters (e.g. `#`) would cut the URL short — sanitize and encode. */
    get bolProductPageUrl(): string {
        const slug = encodeURIComponent((this.title ?? '').replaceAll('/', '-'));
        return `https://www.bol.com/nl/nl/p/${slug}/${this.globalId}${BOL_REVIEWS_URL_FRAGMENT}`;
    }

    /** "author <sep> age <sep> city <sep> date" line for the review footers —
     *  missing fields are skipped, the formatted review date always ends it. */
    getAuthorLine = (separator: string): string => {
        const knownData: Array<string> = [];
        if (this.author) knownData.push(this.author);
        if (this.age) knownData.push(this.age);
        if (this.city) knownData.push(this.city);
        knownData.push(this.reviewDate.format(ProductReview.dateFormat));
        return knownData.join(separator);
    };

    protected build = (props?: Props) => {
        this.constructFields(props);
    };
}
